import { useMemo, useRef, useState, type ReactNode } from "react";
import { Send, Paperclip, X, AlertCircle } from "lucide-react";
import { canWriteInbox } from "@/lib/inbox-roles";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api-client";
import { useBrandContextOptional } from "@/contexts/brand-context";
import { useSendInboxMessage } from "@/hooks/use-send-inbox-message";
import type { InboxConversationSummary } from "@/hooks/use-inbox-conversations";
import type { InboxMessage } from "@/hooks/use-inbox-conversation";
import { useTranslation } from "react-i18next";

/** Slot for ai-reply / templates / etc. to plug into the composer toolbar. */
export interface ComposerExtra {
  id: string;
  render: (ctx: {
    conversation: InboxConversationSummary;
    draft: string;
    setDraft: (s: string) => void;
  }) => ReactNode;
}

interface Props {
  conversation: InboxConversationSummary;
  /**
   * Current loaded messages for the thread. Used to compute the 24h-window
   * predicate (last inbound message timestamp).
   */
  messages?: InboxMessage[];
  extras?: ComposerExtra[];
  /** External force-disable (e.g. read-only role). */
  disabled?: boolean;
}

const MAX_CHARS = 1000;
const SOFT_WARN_AT = 900;
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25MB

function canWrite(role: string | undefined): boolean {
  return canWriteInbox(role);
}

/** Compute the 24h-window predicate client-side from the loaded messages. */
function lastInboundAt(messages: InboxMessage[] | undefined): Date | null {
  if (!messages?.length) return null;
  let best: Date | null = null;
  for (const m of messages) {
    if (m.direction !== "in") continue;
    const t = new Date(m.platformSentAt ?? m.sentAt);
    if (Number.isNaN(t.getTime())) continue;
    if (!best || t > best) best = t;
  }
  return best;
}

export function Composer({
  conversation,
  messages,
  extras = [],
  disabled: externalDisabled = false,
}: Props) {
  const { t } = useTranslation("inbox");
  const [draft, setDraft] = useState("");
  const [attachment, setAttachment] = useState<{
    url: string;
    type: "image" | "video";
    name: string;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const brandCtx = useBrandContextOptional();
  const writeAllowed = canWrite(brandCtx?.currentUserRole);
  const sendMut = useSendInboxMessage();

  const lastIn = useMemo(() => lastInboundAt(messages), [messages]);
  const outOfWindow = useMemo(() => {
    if (!lastIn) return false; // unknown — let the server be the source of truth
    return Date.now() - lastIn.getTime() > 24 * 60 * 60 * 1000;
  }, [lastIn]);

  const hasContent = draft.trim().length > 0 || !!attachment;
  const overLimit = draft.length > MAX_CHARS;
  const disabled =
    externalDisabled ||
    !writeAllowed ||
    !hasContent ||
    overLimit ||
    outOfWindow ||
    uploading ||
    sendMut.isPending;

  async function handlePickFile() {
    fileInputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setUploadError(t("composer.attachmentTooBig"));
      return;
    }
    const mediaType = file.type.startsWith("video/") ? "video" : "image";
    try {
      setUploading(true);
      const presigned = await apiFetch<{ uploadUrl: string; publicUrl: string }>(
        `/brands/${conversation.brandId}/upload-url?filename=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type)}`,
      );
      await fetch(presigned.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      setAttachment({
        url: presigned.publicUrl,
        type: mediaType,
        name: file.name,
      });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : t("composer.uploadFailed"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function handleSend() {
    if (disabled) return;
    sendMut.mutate(
      {
        brandId: conversation.brandId,
        conversationId: conversation.id,
        text: draft.trim() || undefined,
        attachmentUrl: attachment?.url,
        attachmentType: attachment?.type,
      },
      {
        onSettled: () => {
          setDraft("");
          setAttachment(null);
        },
      },
    );
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  }

  if (!writeAllowed) {
    return (
      <div className="border-t border-border p-3 flex-shrink-0">
        <div className="rounded-md border border-border bg-surface-2 p-2 text-[12px] text-muted-foreground text-center">
          {t("composer.roleNoReply")}
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-border p-3 flex-shrink-0">
      {extras.length > 0 && (
        <div className="flex items-center gap-2 mb-2">
          {extras.map((extra) => (
            <div key={extra.id} className="flex-1 min-w-0">
              {extra.render({ conversation, draft, setDraft })}
            </div>
          ))}
        </div>
      )}

      {outOfWindow && (
        <div className="mb-2 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1.5 text-[12px] text-destructive">
          <AlertCircle size={13} />
          <span>{t("composer.outOfWindow")}</span>
        </div>
      )}

      {uploadError && (
        <div className="mb-2 text-[12px] text-destructive">{uploadError}</div>
      )}

      {attachment && (
        <div className="mb-2 flex items-center gap-2 rounded-md border border-border bg-surface-2 px-2 py-1.5 text-[12px]">
          <span className="truncate flex-1">{attachment.name}</span>
          <button
            type="button"
            onClick={() => setAttachment(null)}
            className="text-muted-foreground hover:text-foreground"
            aria-label={t("composer.removeAttachment")}
          >
            <X size={13} />
          </button>
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          className="sr-only"
          onChange={handleFileChange}
        />
        <button
          type="button"
          onClick={handlePickFile}
          disabled={uploading || sendMut.isPending}
          title={t("composer.attachTitle")}
          className="flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Paperclip size={16} />
        </button>
        <div className="flex-1 flex flex-col gap-1">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            disabled={externalDisabled || outOfWindow}
            placeholder={
              outOfWindow ? t("composer.placeholderOutOfWindow") : t("composer.placeholder")
            }
            className="resize-none rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
          />
          <div
            className={`text-[10px] self-end ${
              overLimit
                ? "text-destructive"
                : draft.length >= SOFT_WARN_AT
                  ? "text-amber-600"
                  : "text-muted-foreground"
            }`}
          >
            {draft.length}/{MAX_CHARS}
          </div>
        </div>
        <Button
          type="button"
          size="icon"
          disabled={disabled}
          onClick={handleSend}
          className="h-9 w-9"
          title={
            outOfWindow
              ? t("composer.outOfWindowTitle")
              : sendMut.isPending
                ? t("composer.sendingTitle")
                : t("composer.sendTitle")
          }
        >
          <Send size={16} />
        </Button>
      </div>
    </div>
  );
}
